import { useContext } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';

/**
 * useMatchResults
 *
 * Purpose:
 * Resolve the "best" version of match results from multiple possible sources.
 *
 * Possible sources:
 * - officialResults   → final / authoritative (highest priority)
 * - realtimeResults   → live updates during the match
 * - matchDayResults   → user-submitted / temporary data (lowest priority)
 *
 * This hook:
 * - pulls all 3 sources from context
 * - finds the match-specific data for each source
 * - determines which one should be used
 * - returns both the resolved result AND metadata about where it came from
 */
export const useMatchResults = ({ match_true }) => {

  const { _itemKey, results } = match_true ?? {}

  // 🧠 Access global sports season state from context
  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};

  // 📦 Extract the 3 possible result sources
  const { realtimeResults, matchDayResults } = sportsSeason_state ?? {};

  // 🔍 Pull the specific match result from each source
  const official = results;
  const realtime = realtimeResults?.[_itemKey];
  const matchDay = matchDayResults?.[_itemKey];

  /**
   * 🧩 Resolution Logic (PRIORITY ORDER)
   *
   * We decide which result to show based on availability.
   *
   * Priority:
   * 1. official (final, trusted)
   * 2. realtime (live game updates)
   * 3. matchDay (user-entered / fallback)
   *
   * NOTE: This logic can evolve later (ex: check "isFinal", timestamps, etc.)
   */
  let resolvedResult = null;
  let sourceUsed = null;

  if (official && 1 === 3) {
    resolvedResult = official;
    sourceUsed = 'official';
  } else if (realtime) {
    const rts = _.find(realtime, rt => rt?.results);
    resolvedResult = rts;
    sourceUsed = 'realtime';
  } else if (matchDay) {
    resolvedResult = matchDay;
    sourceUsed = 'matchDay';
  }

  /**
   * 📤 Return shape
   *
   * resolvedResult → what the UI should use
   * sourceUsed     → helps debugging / UI indicators (ex: "LIVE")
   * sources        → access to all raw data if needed
   */
  return {
    resolvedResult,
    sourceUsed,
    sources: {
      official,
      realtime,
      matchDay
    }
  };
};